import { createAction } from '@reduxjs/toolkit'
import i18n from 'i18next'
import _ from 'underscore'

import SerialTicketsClient from '../clients/SerialTicketsClient'
import {
  getAgent,
  getCompany,
  getMode,
  getPaymentForConfirmReservation,
  getSelectedSerialTickets,
  hasSelectedTickets
} from '../selectors/selectors'
import { GIFT_CARDS } from '../utils/mode'
import { addReduxStateToLocalStorage } from '../utils/preloadedState'

export const receivedMarket = createAction('MARKET/RECEIVED')

export const requestCoupons = createAction('COUPONS/REQUEST/START')
export const receivedCoupons = createAction('COUPONS/RECEIVED')

export const addTicket = createAction('TICKET/ADD')
export const removeTicket = createAction('TICKET/REMOVE')
export const preselectTicket = createAction('TICKET/PRESELECT')
export const requestTickets = createAction('TICKETS/REQUEST/START')
export const receivedTickets = createAction('TICKETS/RECEIVED')

export const addVoucher = createAction('VOUCHER/ADD')
export const removeVoucher = createAction('VOUCHER/REMOVE')

export const loggedInClubOne = createAction('CLUB_ONE_USER/LOGGED_IN')
export const loggedOutClubOne = createAction('CLUB_ONE_USER/LOGGED_OUT')
export const receivedClubOneDetails = createAction('CLUB_ONE_USER/RECEIVED_DETAILS')

export const companyLoggedIn = createAction('COMPANY/LOGGED_IN')
export const companyLoggedOut = createAction('COMPANY/LOGGED_OUT')
export const updateCompanyIdFieldValue = createAction('COMPANY_ID/UPDATE')
export const agentLoggedIn = createAction('AGENT/LOGGED_IN')
export const agentLoggedOut = createAction('AGENT/LOGGED_OUT')
export const updateAgentIdFieldValue = createAction('AGENT_ID/UPDATE')
export const requestSummary = createAction('SUMMARY/REQUEST/START')
export const receivedSummary = createAction('SUMMARY/RECEIVED')
export const clearSummary = createAction('SUMMARY/CLEAR')

export const selectClubOneTab = createAction('CLIENT_TYPE/SELECT_CLUB_ONE')
export const selectCompanyTab = createAction('CLIENT_TYPE/SELECT_COMPANY')
export const switchToCompanyTab = createAction('CLIENT_TYPE/SWITCH_TO_COMPANY')

export const updateDeliveryDetailsField = createAction('DELIVERY_DETAILS/UPDATE_FIELD')
export const updateDeliveryDetailsValidity = createAction('DELIVERY_DETAILS/UPDATE_VALIDITY')

export const requestReservation = createAction('RESERVATION/REQUEST/START')
export const receivedReservation = createAction('RESERVATION/RECEIVED')

export const enablePayment = createAction('PAYMENT/ENABLE')
export const selectPaymentMethod = createAction('PAYMENT/SELECT_PAYMENT_METHOD')
export const selectInvoice = createAction('PAYMENT/SELECT_INVOICE')
export const resetPaymentMethod = createAction('PAYMENT/RESET_PAYMENT_METHOD')
export const confirmPaymentSelection = createAction('PAYMENT/CONFIRM_PAYMENT')
export const resetPaymentSelection = createAction('PAYMENT/RESET_PAYMENT')
export const receivedAvailablePaymentTypes = createAction('PAYMENT/RECEIVED_AVAILABLE_PAYMENT_TYPES')

export const updateReferenceNumber = createAction('PAYMENT/UPDATE_REFERENCE_NUMBER')

export const setApiError = createAction('API/ERROR')

export const ssoRendered = createAction('SSO/RENDERED')
export const ssoErrorOccurred = createAction('SSO/ERROR_OCCURRED')

export const setGiftCardsMode = createAction('MODE/SET_GIFT_CARDS')

export const getSummary = () => async (dispatch, getState) => {
  const state = getState()
  const { price, regularPrice, lastRequestPayload } = state.summary

  if (!hasSelectedTickets(state)) {
    SerialTicketsClient.abortPostSummary()
    if (price || regularPrice) dispatch(clearSummary())
    return
  }

  const requestPayload = createSummaryRequestPayload(state)
  const nothingImportantChanged = _.isEqual(requestPayload, lastRequestPayload)

  if (nothingImportantChanged) return

  dispatch(requestSummary(requestPayload))
  const mode = getMode(state)
  const response = await SerialTicketsClient.postSummary(requestPayload, mode)
  if (!response) return
  dispatch(receivedSummary(response))
}

const createSummaryRequestPayload = state => ({
  companyId: getCompany(state)?.companyId,
  companyAgentId: getAgent(state)?.agentId,
  passengers: [{ clientId: state.user?.clientId }],
  addons: getSelectedSerialTickets(state).map(({ code, count }) => ({ code, count })),
  vouchers: state.addedVouchers
})

export const initMainPage = (mode, { code, amount } = {}) => async dispatch => {
  if (mode === GIFT_CARDS) {
    dispatch(setGiftCardsMode())
  }

  const { tickets, language, country } = await SerialTicketsClient.getAvailableSerialTickets({ mode })

  dispatch(receivedTickets(tickets))
  if (code) dispatch(preselectTicket({ code, count: parseInt(amount, 10) || 1 }))
  dispatch(
    receivedMarket({
      locale: language.toLowerCase(),
      country: country.toLowerCase(),
      currency: tickets[0]?.currency.toLowerCase()
    })
  )
  await dispatch(getAvailablePaymentTypes())
}

export const getSerialTickets = () => async (dispatch, getState) => {
  const state = getState()
  const { companyId } = getCompany(state)
  const mode = getMode(state)
  const requestPayload = companyId ? { companyId, mode } : { mode }

  dispatch(requestTickets(requestPayload))
  const { tickets } = await SerialTicketsClient.getAvailableSerialTickets(requestPayload)
  dispatch(receivedTickets(tickets))
}

export const getCoupons = ({ clientGuid, clientId, resToken }) => async (dispatch, getState) => {
  const { coupons, user } = getState()

  if (coupons.loading) return

  if (clientGuid) {
    if (coupons.coupons?.length > 0) return
    dispatch(requestCoupons())
    const newCoupons = await SerialTicketsClient.getUserCoupons({ clientGuid })
    dispatch(receivedCoupons({ coupons: newCoupons }))

    return
  }

  if (resToken) {
    if (coupons.coupons?.length > 0) return
    dispatch(requestCoupons())
    const newCoupons = await SerialTicketsClient.getUserCoupons({ resToken })
    dispatch(receivedCoupons({ coupons: newCoupons }))

    return
  }

  if (clientId && user.token) {
    dispatch(requestCoupons())
    const coupons = await SerialTicketsClient.getUserCoupons({
      clientId,
      ssoToken: user.token
    })
    dispatch(receivedCoupons({ coupons }))
  }
}

export const getDetailedUserInfo = shouldRequestCoupons => async (dispatch, getState) => {
  const { user } = getState()

  const client = await SerialTicketsClient.getClientBySsoToken(user.token, () => dispatch(ssoErrorOccurred()))

  if (client) {
    dispatch(receivedClubOneDetails(client))

    if (shouldRequestCoupons) {
      await dispatch(getCoupons({ clientId: client.clientId }))
    }
  }
}

export const getCompanyInfo = companyId => SerialTicketsClient.getCompany(companyId)

export const initConfirmationPage = (token, firstTime) => async dispatch => {
  dispatch(requestReservation())
  const response = await SerialTicketsClient.getReservation(token)
  if (!response) return
  dispatch(receivedReservation({ ...response, firstTime }))
  dispatch(
    receivedMarket({
      locale: response.language.toLowerCase(),
      country: response.country.toLowerCase(),
      currency: response.currency.toLowerCase()
    })
  )
}

const getAvailablePaymentTypes = () => async (dispatch, getState) => {
  const { digiData, mode } = getState()
  const country = digiData?.website?.country || 'fi'

  const paymentTypes = await SerialTicketsClient.getAvailablePaymentTypes(mode)
  await dispatch(receivedAvailablePaymentTypes({ paymentTypes, country }))
}

export const confirmReservation = () => async (dispatch, getState) => {
  const paymentForConfirmReservation = getPaymentForConfirmReservation(getState())
  const { summary, company, user, agent, addedVouchers, deliveryDetails, payment, mode } = getState()
  const passengers = user?.clientId
    ? [{ clientId: user?.clientId, email: user?.email }]
    : [deliveryDetails?.values || {}]
  const confirmReservationRequest = {
    clientInfo: {
      email: deliveryDetails?.values?.email,
      ssoToken: user?.token
    },
    reservation: {
      addons: getSelectedSerialTickets(getState()).map(({ code, count }) => ({ code, count })),
      companyAgentId: agent?.agentId,
      companyId: company?.companyId,
      passengers,
      referenceNumber: payment.referenceNumber,
      vouchers: addedVouchers
    },
    payment: paymentForConfirmReservation,
    visiblePrice: summary?.price,
    redirectUrls: {
      cancelUrl: `${window.location.origin}?backendError=paymentCancelled`,
      successUrl: `${window.location.origin}/confirmation?firstTime=true`,
      rejectUrl: `${window.location.origin}?backendError=paymentRejected`,
      termsUrl: i18n.t('kustom.terms.conditions')
    }
  }
  const confirmResponse = await SerialTicketsClient.confirmReservation(confirmReservationRequest, mode)

  if (confirmResponse?.error) return confirmResponse

  switch (paymentForConfirmReservation.type) {
    case 'invoice':
      SerialTicketsClient.redirectToConfirmationPage(confirmResponse)
      return
    case 'ADYEN':
    case 'SWEDBANK':
    case 'KLARNA':
      SerialTicketsClient.redirectToPaymentServicePage(confirmResponse)
      return
    default:
      throw new Error(`${paymentForConfirmReservation.type} payment method is not supported`)
  }
}

export const confirmPayment = () => async (dispatch, getState) => {
  addReduxStateToLocalStorage(getState())
  dispatch(confirmPaymentSelection())

  const confirmResponse = await dispatch(confirmReservation())
  if (confirmResponse?.error) dispatch(resetPaymentSelection())
  return confirmResponse
}
